import React from "react";
import { Zap, Cpu, FileText, Activity, ShieldCheck, Check, AlertTriangle, HelpCircle } from "lucide-react";
import { WorkspaceTask, ExecutionStep } from "./types";

interface ExecutionFlowGraphProps {
  task: WorkspaceTask;
  isAr?: boolean;
}

export const ExecutionFlowGraph: React.FC<ExecutionFlowGraphProps> = ({ task, isAr = false }) => {
  // Let's extract the node states from the task's steps or map them
  // Visual nodes mapping
  // Node 1: Trigger
  // Node 2: Executor
  // Node 3: Step 1 (e.g., Document Intake)
  // Node 4: Step 2 (e.g., Compliance Scan)
  // Node 5: Output (e.g., Report Generation)

  const stepsLength = task.steps.length;
  
  // Helper to determine node status
  const getNodeStatus = (index: number) => {
    // Determine the status of each node base on task overall status and step status
    if (task.status === "QUEUED") {
      return index === 0 ? "running" : "pending";
    }
    
    if (task.status === "COMPLETED") {
      return "completed";
    }

    if (task.status === "FAILED") {
      // Find where it failed
      const failedIndex = task.steps.findIndex(s => s.status === "failed");
      if (failedIndex !== -1) {
        const stepIndexMapped = index - 2; // Node 0: Trigger, Node 1: Executor, then steps 0,1,2
        if (stepIndexMapped === failedIndex) return "failed";
        if (stepIndexMapped < failedIndex) return "completed";
        return "pending";
      }
      return "failed";
    }

    // Task is RUNNING
    if (index === 0) return "completed"; // Trigger is done
    if (index === 1) return "completed"; // Executor is assigned and running
    
    const stepIndex = index - 2;
    if (stepIndex >= 0 && stepIndex < stepsLength) {
      const step = task.steps[stepIndex];
      if (step.status === "running") return "running";
      if (step.status === "completed") return "completed";
      if (step.status === "failed") return "failed";
      return "pending";
    }
    
    return "pending";
  };

  const getHSLStyles = (status: "pending" | "running" | "completed" | "failed") => {
    switch (status) {
      case "completed":
        return {
          textColor: "text-[hsl(142,70%,40%)]",
          borderColor: "border-[hsl(142,70%,50%)]",
          bgColor: "bg-[hsl(142,70%,96%)]",
          glow: "shadow-[0_0_10px_rgba(16,185,129,0.15)]",
        };
      case "running":
        return {
          textColor: "text-[hsl(38,92%,45%)]",
          borderColor: "border-[hsl(38,92%,50%)]",
          bgColor: "bg-[hsl(38,92%,96%)]",
          glow: "shadow-[0_0_15px_rgba(245,158,11,0.25)] animate-pulse",
        };
      case "failed":
        return {
          textColor: "text-[hsl(0,84%,45%)]",
          borderColor: "border-[hsl(0,84%,55%)]",
          bgColor: "bg-[hsl(0,84%,97%)]",
          glow: "shadow-[0_0_15px_rgba(239,68,68,0.25)]",
        };
      case "pending":
      default:
        return {
          textColor: "text-[#64748b]",
          borderColor: "border-[#cbd5e1]",
          bgColor: "bg-[#f8fafc]",
          glow: "shadow-none",
        };
    }
  };

  const nodes = [
    {
      label: isAr ? "تحفيز" : "Trigger",
      icon: Zap,
    },
    {
      label: isAr ? "المنفذ" : "Executor",
      icon: Cpu,
    },
    {
      label: stepsLength > 0 ? (isAr ? task.steps[0].nameAr : task.steps[0].name) : (isAr ? "مرحلة 1" : "Step 1"),
      icon: FileText,
    },
    {
      label: stepsLength > 1 ? (isAr ? task.steps[1].nameAr : task.steps[1].name) : (isAr ? "مرحلة 2" : "Step 2"),
      icon: Activity,
    },
    {
      label: isAr ? "مخرجات" : "Output",
      icon: ShieldCheck,
    },
  ];

  return (
    <div className={`w-full py-4 px-3 bg-slate-50/50 rounded-xl border border-slate-205/60 ${isAr ? "flex-row-reverse" : "flex-row"}`} dir={isAr ? "rtl" : "ltr"}>
      {/* Node Horizontal Chain display */}
      <div className={`flex items-center justify-between w-full relative ${isAr ? "flex-row-reverse" : "flex-row"}`}>
        
        {/* Background connector line */}
        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-200/80 -translate-y-1/2 z-0" />

        {nodes.map((node, idx) => {
          const status = getNodeStatus(idx);
          const styles = getHSLStyles(status);
          const Icon = node.icon;

          // Check downstream status to animate the connector
          const isNextNodeActiveOrDone = idx < nodes.length - 1 && 
            (getNodeStatus(idx + 1) === "running" || getNodeStatus(idx + 1) === "completed");
          const isNextNodeRunning = idx < nodes.length - 1 && getNodeStatus(idx + 1) === "running";

          return (
            <React.Fragment key={idx}>
              <div className="flex flex-col items-center z-10 basis-1/5 min-w-0">
                <div 
                  className={`w-9 h-9 rounded-full border-1.5 flex items-center justify-center transition-all duration-305 ${styles.bgColor} ${styles.borderColor} ${styles.textColor} ${styles.glow} font-semibold`}
                  title={`${node.label}: ${status.toUpperCase()}`}
                >
                  {status === "completed" ? (
                    <Check size={15} strokeWidth={3} />
                  ) : status === "failed" ? (
                    <AlertTriangle size={15} />
                  ) : (
                    <Icon size={15} className={status === "running" ? "animate-spin" : ""} />
                  )}
                </div>
                <span className="text-[10px] font-mono font-bold mt-1.5 text-center text-slate-500 uppercase tracking-widest truncate max-w-[90%] block">
                  {node.label}
                </span>
                <span className="text-[8px] font-bold text-slate-400 mt-0.5 uppercase tracking-wide">
                  {status}
                </span>
              </div>

              {/* Connecting line segment overlay */}
              {idx < nodes.length - 1 && (
                <div 
                  className={`absolute h-0.5 top-1/2 -translate-y-1/2 z-0 transition-all duration-300`}
                  style={{
                    left: `${(idx * 20) + 10}%`,
                    width: '15%',
                    backgroundColor: isNextNodeActiveOrDone 
                      ? (isNextNodeRunning ? 'rgba(245, 158, 11, 0.5)' : '#10b981') 
                      : '#cbd5e1',
                    boxShadow: isNextNodeRunning 
                      ? '0 0 8px rgba(245, 158, 11, 0.4)' 
                      : 'none',
                    animation: isNextNodeRunning ? 'pulse 2s infinite' : 'none'
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
